import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMount } from 'react-use';
import useMuiTableBuilderV2 from 'hooks/useMuiTableBuilderV2';
import useAppNavigation from 'hooks/useAppNavigation';
import RefreshIcon from '@mui/icons-material/Refresh';
import Loader from 'components/Loader';
import {
  getTableConfig,
  TableVariants,
  dataCountByEntityStateId,
  defaultDay,
  entityMappingStateId,
} from './tableConfig';
import { Box, Button, IconButton, TextField } from '@mui/material';
import useDataCountsByDataTypeListItemsBuilder from './helpers/useDataCountsByDataTypeListItemsBuilder';
import { integrationsHash, DataType } from '@constants';
import { DataCountsByDataType } from 'types';
import { EntityMapping, FetchEntityMappingsSuccessResponse } from 'IntegrationModule/types';
import { SingleValuePickerV2 } from 'components/SingleValuePicker';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import {
  checkIsTwoWaySyncIntegration,
  checkIsReplicationIntegration,
  checkIsCsvIntegration,
} from 'utils';
import { fetchDataCountsByEntity, fetchEntityMappings } from 'thunk';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { sub, format } from 'date-fns';
import { useEntityRetryMenu } from 'hooks/useEntityRetryMenu';

type TableProps = {
  targetService: string;
  teamId: number;
  targetServiceId: number;
};

type ProjectFieldValues = {
  projectName: string;
  projectNumber: string;
  sessionId?: string;
};

type EmployeeFieldValues = {
  employeeFirstName: string;
  employeeLastName: string;
  employeeEmail: string;
  sessionId?: string;
};

type ProjectEntityIdFieldValues = {
  targetProjectId?: string;
  sessionId?: string;
};

type FieldValues = {
  targetEmployeeId: string;
  targetProjectId: string;
  sessionId?: string;
};

type EmployeeEntityIdFieldValues = {
  targetEmployeeId?: string;
  sessionId?: string;
};

type EntityIdFieldValues = ProjectEntityIdFieldValues | EmployeeEntityIdFieldValues;

type EntityFieldValues = ProjectFieldValues | EmployeeFieldValues;

type AllowedDataType =
  | typeof NONE
  | DataType.Project
  | DataType.Employee
  | typeof PROJECT_ENTITY_ID
  | typeof EMPLOYEE_ENTITY_ID;

const days = Array.from({ length: 30 }, (v, k) => ({
  label: String(k + 1),
  value: k + 1,
}));

const daysHash = keyBy(days, 'value');

const dayItems = days.map((d) => d.value);

const NONE = 'none';
const PROJECT_ENTITY_ID = 'targetProjectId';
const EMPLOYEE_ENTITY_ID = 'targetEmployeeId';

const initialDataTypesState = {
  [NONE]: {},
  [DataType.Project]: {
    projectName: '',
    projectNumber: '',
    sessionId: '',
  },
  [DataType.Employee]: {
    employeeFirstName: '',
    employeeLastName: '',
    employeeEmail: '',
    sessionId: '',
  },
  [PROJECT_ENTITY_ID]: {
    targetProjectId: '',
    sessionId: '',
  },
  [EMPLOYEE_ENTITY_ID]: {
    targetEmployeeId: '',
    sessionId: '',
  },
};

const defaultDataType = NONE;

/**
 * Variant Level
 * 1) Replication Check
 * 2) Two Way Sync
 * 3) Full Table
 */
const getDefaultVariant = ({
  isTwoWaySync,
  isReplicationIntegration,
}: {
  isTwoWaySync: boolean;
  isReplicationIntegration: boolean;
}) => {
  if (isReplicationIntegration) return TableVariants.ReplicationIntegrationsTable;

  return isTwoWaySync ? TableVariants.TwoWaySyncFullTable : TableVariants.FullTable;
};

const MappingStatsPerDataTypesTableV2 = ({
  teamId,
  targetService,
  targetServiceId,
}: TableProps) => {
  const isTwoWaySync = checkIsTwoWaySyncIntegration(targetService);
  const isReplicationIntegration = checkIsReplicationIntegration(targetService);
  const integration = integrationsHash[targetService];
  const defaultVariant = getDefaultVariant({
    isTwoWaySync,
    isReplicationIntegration,
  });
  const [variant, setVariant] = useState(defaultVariant);
  const [day, setDay] = useState<number>(defaultDay);
  const { navigateToPendingEntitiesPage } = useAppNavigation();
  const [selectedDataTypeOptions, setSelectedDataTypeOptions] = useState(initialDataTypesState);
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { data: dataCountsByDataType = {}, isRequesting: isFetchingDataCountsByDataType } =
    selectLocalData<Record<DataType, DataCountsByDataType[]>>({
      stateId: dataCountByEntityStateId,
    });
  const { isRequesting: isFetchingEntityMappings } = selectLocalData({
    stateId: entityMappingStateId,
  });
  const isRequesting = isFetchingEntityMappings || isFetchingDataCountsByDataType;

  const [selectedDataType, setSelectedDataType] = useState<AllowedDataType>(defaultDataType);
  const isHidingDetails =
    variant === TableVariants.WithoutDetailsGroup ||
    variant === TableVariants.TwoWaySyncWithoutDetailsGroup ||
    variant === TableVariants.ReplicationIntegrationsWithoutDetailsGroup;
  const isCsvAgent = checkIsCsvIntegration(targetService);
  const tableConfig = useMemo(() => getTableConfig({ variant }), [variant]);
  const { openEntityRetryMenu } = useEntityRetryMenu();
  const mainListItemsBuilder = useDataCountsByDataTypeListItemsBuilder({
    dataCountsByDataType,
    isCsvAgent,
  });

  const filterDataTypeOptionsArray = [
    {
      value: NONE,
      label: 'None',
    },
    {
      value: DataType.Project,
      label: 'Project',
    },
    {
      value: DataType.Employee,
      label: 'Employee',
    },
    ...(isCsvAgent
      ? []
      : [
          {
            value: PROJECT_ENTITY_ID,
            label: 'Target Project ID',
          },
          {
            value: EMPLOYEE_ENTITY_ID,
            label: 'Target Employee ID',
          },
        ]),
  ];

  const filterDataTypeOptions = filterDataTypeOptionsArray.map((i) => i.value);

  const filterDataTypeOptionsHash = keyBy(filterDataTypeOptionsArray, 'value');

  const searchFieldPropsByTypes = useMemo(() => {
    return {
      [DataType.Project]: {
        fields: [
          { label: "Project's Name", field: 'projectName' },
          { label: "Project's Number", field: 'projectNumber' },
          ...(isCsvAgent ? [{ label: 'Session ID', field: 'sessionId' }] : []),
        ],
        makeFetchEntityMappingParams: ({
          projectName,
          projectNumber,
        }: Partial<ProjectFieldValues>) => {
          // If want fuzzy search, then use {searchAttributes: `${projectName} ${projectNumber}`}
          return {
            filterParams: [
              ...(projectName ? [{ filterAttribute: 'title', filterValue: projectName }] : []),
              ...(projectNumber
                ? [
                    {
                      filterAttribute: 'targetProjectNumber',
                      filterValue: projectNumber,
                    },
                  ]
                : []),
            ],
          };
        },
        makeFetchDataCountsByEntityParams: ({
          entities,
          sessionId,
        }: {
          entities?: FetchEntityMappingsSuccessResponse['entities'];
          sessionId?: string;
        }) => {
          const targetIds = entities?.map((entity) => entity.targetId);
          return {
            parentIdsByDataType: {
              [DataType.Project]: targetIds,
            },
            ...(sessionId ? [{ filterAttribute: 'sessionId', filterValue: sessionId }] : []),
          };
        },
      },
      [DataType.Employee]: {
        fields: [
          { label: 'First Name', field: 'employeeFirstName' },
          { label: 'Last Name', field: 'employeeLastName' },
          { label: 'Email', field: 'employeeEmail' },
          ...(isCsvAgent ? [{ label: 'Session ID', field: 'sessionId' }] : []),
        ],
        makeFetchEntityMappingParams: ({
          employeeFirstName,
          employeeLastName,
          employeeEmail,
        }: Partial<EmployeeFieldValues>) => {
          // If want fuzzy, then use {searchAttributes: `${employeeFirstName} ${lastName} ${email}`}
          return {
            filterParams: [
              ...(employeeLastName
                ? [
                    {
                      filterAttribute: 'lastName',
                      filterValue: employeeLastName,
                    },
                  ]
                : []),
              ...(employeeFirstName
                ? [
                    {
                      filterAttribute: 'firstName',
                      filterValue: employeeFirstName,
                    },
                  ]
                : []),
              ...(employeeEmail
                ? [
                    {
                      filterAttribute: 'email',
                      filterValue: employeeEmail,
                    },
                  ]
                : []),
            ],
          };
        },
        makeFetchDataCountsByEntityParams: ({
          entities,
          sessionId,
        }: {
          entities?: FetchEntityMappingsSuccessResponse['entities'];
          sessionId?: string;
        }) => {
          const targetIds = entities?.map((entity) => entity.targetId);
          return {
            parentIdsByDataType: {
              [DataType.Employee]: targetIds,
            },
            sessionId,
          };
        },
      },
      [PROJECT_ENTITY_ID]: {
        fields: [{ label: 'Target Project ID', field: 'targetProjectId' }],
        makeFetchEntityMappingParams: () => null,
        makeFetchDataCountsByEntityParams: ({
          targetProjectId,
          sessionId,
        }: ProjectEntityIdFieldValues) => {
          return {
            parentIdsByDataType: {
              [DataType.Project]: [targetProjectId],
            },
            sessionId,
          };
        },
      },
      [EMPLOYEE_ENTITY_ID]: {
        fields: [{ label: 'Target Employee ID', field: 'targetEmployeeId' }],
        makeFetchEntityMappingParams: () => null,
        makeFetchDataCountsByEntityParams: ({
          targetEmployeeId,
          sessionId,
        }: EmployeeEntityIdFieldValues) => {
          return {
            parentIdsByDataType: {
              [DataType.Employee]: [targetEmployeeId],
            },
            sessionId,
          };
        },
      },
      [NONE]: {
        fields: [],
        makeFetchEntityMappingParams: () => null,
        makeFetchDataCountsByEntityParams: () => {},
      },
    };
  }, [isCsvAgent]);

  const mainList = useMemo(() => {
    return { listItems: mainListItemsBuilder() };
  }, [mainListItemsBuilder]);

  const triggerInitialFetch = useCallback(
    ({ daysFromNow }: { daysFromNow?: number } = {}) => {
      const fieldValues = selectedDataTypeOptions[selectedDataType];
      const daysToUse = daysFromNow ?? day;
      const startDate = format(sub(new Date(), { days: daysToUse }), 'yyyy-MM-dd');
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const { makeFetchEntityMappingParams, makeFetchDataCountsByEntityParams } =
        searchFieldPropsByTypes[selectedDataType];

      const triggerDataCountsByEntityFetch = (args: {
        entities?: EntityMapping[];
        sessionId?: string;
        targetProjectId?: string;
        targetEmployeeId?: string;
        startDate?: string;
        endDate?: string;
      }) => {
        dispatchLocal(fetchDataCountsByEntity, {
          params: {
            targetServiceId,
            startDate, // "2022-10-01",
            endDate, // "2022-11-01",
            ...(makeFetchDataCountsByEntityParams && makeFetchDataCountsByEntityParams(args)),
          },
          formatSuccessResponse: ({ response }) => {
            return groupBy(response.data, 'dataType');
          },
          stateId: dataCountByEntityStateId,
        });
      };

      switch (selectedDataType) {
        case DataType.Employee:
        case DataType.Project: {
          const fieldValues = selectedDataTypeOptions[selectedDataType];

          dispatchLocal(fetchEntityMappings, {
            params: {
              dataType: selectedDataType,
              targetServiceId,
              teamId,
              body: {
                offset: 0,
                limit: 100,
                ...makeFetchEntityMappingParams(fieldValues),
              },
            },
            onSuccessCallback: ({
              response,
            }: {
              response: { data: FetchEntityMappingsSuccessResponse };
            }) => {
              const entities = response.data.entities;
              triggerDataCountsByEntityFetch({
                entities,
                sessionId: fieldValues.sessionId,
              });
            },
            stateId: entityMappingStateId,
          });
          break;
        }
        case NONE:
        case PROJECT_ENTITY_ID:
        case EMPLOYEE_ENTITY_ID: {
          const fieldValues = selectedDataTypeOptions[selectedDataType];
          triggerDataCountsByEntityFetch(fieldValues);
          break;
        }
      }
    },
    [
      day,
      dispatchLocal,
      searchFieldPropsByTypes,
      selectedDataType,
      selectedDataTypeOptions,
      targetServiceId,
      teamId,
    ],
  );

  useMount(() => {
    triggerInitialFetch();
  });

  const onPendingCellClick = useCallback(
    (pendingEntitiesDataTypes: Array<string>, pendingEntitiesMappingStatuses: Array<string>) => {
      const navigate = navigateToPendingEntitiesPage(
        teamId,
        targetService,
        targetServiceId,
        pendingEntitiesDataTypes,
        pendingEntitiesMappingStatuses,
      );
      navigate();
    },
    [navigateToPendingEntitiesPage, targetService, targetServiceId, teamId],
  );

  const onChangeVariant = useCallback(() => {
    if (isReplicationIntegration) {
      if (isHidingDetails) {
        setVariant(TableVariants.ReplicationIntegrationsTable);
      } else {
        setVariant(TableVariants.ReplicationIntegrationsWithoutDetailsGroup);
      }
    } else if (isTwoWaySync) {
      if (isHidingDetails) {
        setVariant(TableVariants.TwoWaySyncFullTable);
      } else {
        setVariant(TableVariants.TwoWaySyncWithoutDetailsGroup);
      }
    } else {
      if (isHidingDetails) {
        setVariant(TableVariants.FullTable);
      } else {
        setVariant(TableVariants.WithoutDetailsGroup);
      }
    }
  }, [isHidingDetails, isReplicationIntegration, isTwoWaySync]);

  const onDayChange = useCallback(
    (event: React.ChangeEvent<{ value: number }>) => {
      const selectedDay = event.target.value;
      if (day !== selectedDay) {
        triggerInitialFetch({ daysFromNow: selectedDay });
        setDay(selectedDay);
      }
    },
    [day, triggerInitialFetch],
  );

  const onSearchClick = useCallback(() => {
    triggerInitialFetch();
  }, [triggerInitialFetch]);

  const onResetClick = useCallback(() => {
    setSelectedDataTypeOptions(initialDataTypesState);
    setSelectedDataType(defaultDataType);
  }, []);

  // Trigger fetch when reset completes (after state updates)
  const [shouldResetAndFetch, setShouldResetAndFetch] = useState(false);

  // Effect to handle fetch after reset state updates
  useEffect(() => {
    if (shouldResetAndFetch) {
      triggerInitialFetch();
      // Schedule state reset after current render cycle to avoid cascading updates
      const timeoutId = setTimeout(() => {
        setShouldResetAndFetch(false);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [shouldResetAndFetch, triggerInitialFetch]);

  // Trigger the reset sequence
  const handleResetClick = useCallback(() => {
    onResetClick();
    setShouldResetAndFetch(true);
  }, [onResetClick]);

  const onDataTypeChange = useCallback((event: any) => {
    const { value } = event.target;
    setSelectedDataType(value);
  }, []);

  const onFieldValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target;
      setSelectedDataTypeOptions((prev) => ({
        ...prev,
        [selectedDataType]: {
          ...prev[selectedDataType],
          [name]: value,
        },
      }));
    },
    [selectedDataType],
  );

  const renderFilterFields = useCallback(() => {
    const searchFields = searchFieldPropsByTypes[selectedDataType].fields;
    const dataTypeOptions = selectedDataTypeOptions[selectedDataType];

    if (!searchFields || searchFields.length === 0 || !dataTypeOptions) return <></>;

    return (
      <Box display="flex" gap={'10px'}>
        {searchFields.map((searchField: any) => {
          const { label, field } = searchField;
          const value = dataTypeOptions[field as keyof typeof dataTypeOptions];

          return (
            <TextField
              label={label}
              name={field}
              key={`${selectedDataType}-${field}`}
              style={{ width: '7.5rem' }}
              value={value}
              onChange={onFieldValueChange}
            />
          );
        })}
      </Box>
    );
  }, [onFieldValueChange, searchFieldPropsByTypes, selectedDataType, selectedDataTypeOptions]);

  const _customRowProps = useMemo(
    () => ({
      onPendingCellClick,
      integration,
      day,
      days: dayItems,
      daysHash,
      onDayChange,
      isTwoWaySync,
      openEntityRetryMenu,
      teamId,
      targetServiceId,
    }),
    [
      onPendingCellClick,
      integration,
      day,
      onDayChange,
      isTwoWaySync,
      openEntityRetryMenu,
      teamId,
      targetServiceId,
    ],
  );

  const { TableComponent } = useMuiTableBuilderV2({
    ...tableConfig,
    mainList,
    _customRowProps,
    isLoading: false,
    renderHeaderTools: () => {
      return (
        <Box display="flex" alignItems="flex-end" gap={2} width="100%">
          <Box sx={{ minWidth: 200 }}>
            <SingleValuePickerV2
              item={selectedDataType}
              items={filterDataTypeOptions}
              itemsHash={filterDataTypeOptionsHash}
              onValueChange={onDataTypeChange}
              inputLabel={'Data Type Filter'}
            />
          </Box>
          {renderFilterFields()}
          <Button
            variant="contained"
            onClick={onSearchClick}
            color="primary"
            disabled={isRequesting}
            sx={{ height: '40px' }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetClick}
            disabled={isRequesting}
            sx={{ height: '40px' }}
          >
            Reset
          </Button>
          {isRequesting && <Loader />}
        </Box>
      );
    },
    renderFooterOptions: () => {
      return (
        <Box>
          <Button onClick={onChangeVariant}>
            {isHidingDetails ? 'Show Details' : 'Hide Details'}
          </Button>
        </Box>
      );
    },
  });

  return <Box>{TableComponent}</Box>;
};

export default MappingStatsPerDataTypesTableV2;
